import Phaser from "phaser";

export interface NativeTextInputHandle {
  /** Positions the real `<input>` over the given Phaser scene coordinates (converted to CSS pixels via the canvas's actual on-screen rect, so it lines up regardless of device pixel ratio). */
  layout(scene: Phaser.Scene, x: number, y: number, width: number, height: number): void;
  setVisible(visible: boolean): void;
  getValue(): string;
  setValue(value: string): void;
  focus(): void;
  blur(): void;
  /** Selects the entire current value - used by a `readOnly` field (e.g. a copy-failure URL fallback) so a tap/click leaves the whole link ready for the player's own OS-level copy, with no `focus()`-triggered mobile keyboard popping up for a field they can't edit anyway. */
  selectAll(): void;
  onChange(cb: (value: string) => void): void;
  onSubmit(cb: () => void): void;
  destroy(): void;
}

/**
 * Phaser draws to a single `<canvas>` - it has no native text field. For
 * the one place this game needs real keyboard text entry (a player
 * name - Unicode letters, IME composition, mobile autocorrect/emoji
 * keyboards all need to work), the pragmatic answer is a real HTML
 * `<input>` element positioned on top of the canvas, not a hand-rolled
 * bitmap keyboard. It lives outside Phaser's scene graph entirely -
 * `layout()` just keeps it lined up with wherever the modal currently
 * wants it drawn.
 */
export function createNativeTextInput(options: { maxLength: number; placeholder?: string; readOnly?: boolean }): NativeTextInputHandle {
  const input = document.createElement("input");
  input.type = "text";
  input.maxLength = options.maxLength;
  input.placeholder = options.placeholder ?? "";
  input.autocomplete = "off";
  input.spellcheck = false;
  input.readOnly = options.readOnly ?? false;
  Object.assign(input.style, {
    position: "fixed",
    zIndex: "1000",
    boxSizing: "border-box",
    background: "#0a0c12",
    color: "#f5f7fa",
    border: "2px solid #5b8cff",
    borderRadius: "10px",
    fontSize: "18px",
    fontFamily: "Arial, sans-serif",
    padding: "0 12px",
    outline: "none",
    userSelect: "text",
    WebkitUserSelect: "text",
    touchAction: "auto",
    display: "none",
  });
  document.body.appendChild(input);

  let changeCb: ((value: string) => void) | undefined;
  let submitCb: (() => void) | undefined;

  const onInput = () => changeCb?.(input.value);
  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Enter") submitCb?.();
  };
  input.addEventListener("input", onInput);
  input.addEventListener("keydown", onKeydown);

  return {
    layout(scene, x, y, width, height) {
      const canvas = scene.sys.game.canvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = rect.width / scene.scale.width;
      const scaleY = rect.height / scene.scale.height;
      input.style.left = `${rect.left + x * scaleX}px`;
      input.style.top = `${rect.top + y * scaleY}px`;
      input.style.width = `${width * scaleX}px`;
      input.style.height = `${height * scaleY}px`;
      input.style.fontSize = `${Math.max(13, Math.min(18, height * scaleY * 0.42))}px`;
    },
    setVisible(visible: boolean) {
      input.style.display = visible ? "block" : "none";
    },
    getValue: () => input.value,
    setValue(value: string) {
      input.value = value;
    },
    focus() {
      input.focus();
    },
    blur() {
      input.blur();
    },
    selectAll() {
      input.focus();
      input.select();
    },
    onChange(cb) {
      changeCb = cb;
    },
    onSubmit(cb) {
      submitCb = cb;
    },
    destroy() {
      input.removeEventListener("input", onInput);
      input.removeEventListener("keydown", onKeydown);
      input.remove();
    },
  };
}
